/**
 * Site Intelligence Platform Types
 */

export interface Protocol {
  id: string;
  site_id: string;
  protocol_number: string;
  sponsor_name: string | null;
  study_nickname: string | null;
  status: 'active' | 'inactive' | 'completed';
  created_at: string;
  updated_at: string;
  // Extended fields from API
  site_name?: string;
  site_code?: string;
  network_id?: string;
  network_name?: string;
  network_code?: string;
  document_count?: number;
}

// Standard document types for clinical research
export const DOCUMENT_TYPES = [
  { value: 'protocol', label: 'Protocol' },
  { value: 'icf', label: 'ICF (Informed Consent)' },
  { value: 'ib', label: 'IB (Investigator Brochure)' },
  { value: 'sop', label: 'SOP' },
  { value: 'source', label: 'Source Documentation' },
  { value: 'training', label: 'Training Materials' },
  { value: 'regulatory', label: 'Regulatory' },
  { value: 'correspondence', label: 'Correspondence' },
  { value: 'monitoring', label: 'Monitoring Reports' },
  { value: 'other', label: 'Other' },
] as const;

export type DocumentType = typeof DOCUMENT_TYPES[number]['value'];

export interface IntelligenceDocument {
  id: string;
  scope: 'global' | 'network' | 'site' | 'protocol';
  network_id: string | null;
  site_id: string | null;
  protocol_id: string | null;
  filename: string;
  file_path: string;
  file_size: number | null;
  mime_type: string | null;
  title: string | null;
  description: string | null;
  document_type: string | null;
  processing_status: 'pending' | 'processing' | 'completed' | 'failed';
  processing_error: string | null;
  chunk_count: number;
  uploaded_by: string | null;
  created_at: string;
  updated_at: string;
  // Versioning fields
  version: number;
  document_group_id: string;
  is_current: boolean;
  replaced_at: string | null;
  replaced_by: string | null;
  /**
   * Registry backing (P3, plan §5.2). Set + equal to id: thin projection row
   * mirroring an eReg document (read-only here — managed in eReg). Set but
   * different from id: legacy row adopted into eReg. Null/undefined: legacy
   * Intelligence-owned row.
   */
  registry_document_id?: string | null;
}

/** Registry-backed projection row — read-only in Intelligence (managed in eReg). */
export const isRegistryProjection = (doc: IntelligenceDocument) =>
  !!doc.registry_document_id && doc.registry_document_id === doc.id;

export interface IntelligenceChunk {
  id: string;
  document_id: string;
  chunk_index: number;
  content: string;
  scope: string;
  network_id: string | null;
  site_id: string | null;
  protocol_id: string | null;
  metadata: {
    page_number?: number;
    section?: string;
    heading?: string;
    [key: string]: unknown;
  };
  created_at: string;
  // similarity score from search results
  similarity?: number;
}

export interface Conversation {
  id: string;
  user_id: string;
  site_id: string | null;
  protocol_id: string | null;
  title: string | null;
  created_at: string;
  updated_at: string;
}

export interface Message {
  id: string;
  conversation_id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  source_chunks: string[];
  input_tokens?: number;
  output_tokens?: number;
  created_at: string;
}

export interface ProtocolUserAccess {
  id: string;
  protocol_id: string;
  user_id: string;
  granted_by: string | null;
  granted_at: string;
}

export interface IntelligenceUser {
  id: string;
  email: string;
  full_name: string | null;
  avatar_url: string | null;
  site_id: string | null;
  role: 'admin' | 'user' | null;
}

// Chat types
export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: SourceCitation[];
  timestamp: Date;
  isLoading?: boolean;
  protocolContext?: ProtocolContext | null;
}

export interface SourceCitation {
  document_id: string;
  document_title: string;
  document_type?: string;
  chunk_id: string;
  content_preview: string;
  /** Exact quote extracted from LLM response - used for highlighting in PDF viewer */
  relevant_excerpt?: string;
  page_number?: number;
  section?: string;
  similarity?: number;
}

// API request/response types
export interface ChatRequest {
  message: string;
  conversation_id?: string;
  protocol_id?: string;
  site_id?: string;
}

export interface ProtocolContext {
  protocol_id: string;
  protocol_number: string;
  sponsor_name?: string;
  study_nickname?: string;
  site_id: string;
  site_name?: string;
  matched_from_message?: boolean;
}

export interface ChatResponse {
  message: string;
  conversation_id: string;
  sources: SourceCitation[];
  input_tokens: number;
  output_tokens: number;
  protocol_context?: ProtocolContext | null;
  token_limit_reached?: boolean;
}

export interface DocumentUploadRequest {
  file: File;
  scope: 'global' | 'network' | 'site' | 'protocol';
  network_id?: string;
  site_id?: string;
  protocol_id?: string;
  title?: string;
  description?: string;
  document_type?: string;
  document_group_id?: string;  // For uploading new versions
}

export interface SystemPrompt {
  id: string;
  scope: 'global' | 'network' | 'site';
  network_id: string | null;
  site_id: string | null;
  name: string;
  description: string | null;
  prompt_text: string;
  is_active: boolean;
  priority: number;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface SearchResult {
  chunks: IntelligenceChunk[];
  documents: IntelligenceDocument[];
}

// (Staging types removed — the Upload Queue moved to eReg Intake, P5 §8-P5.)

// Re-export oversight types
export * from './oversight';
